import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Calendar, MapPin, Loader2, AlertCircle, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { invitationApi, tastingApi } from '@/api/services';
import { useEventStore } from '@/stores/eventStore';
import { useTastingStore } from '@/stores/tastingStore';
import { useTheme } from '@/theme/ThemeProvider';
import TastingLayout from '@/components/layout/TastingLayout';

interface AcceptFormData {
  guestName: string;
}

export default function InvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { applyBranding } = useTheme();
  const { setGuest } = useEventStore();
  const { setSession, reset: resetTasting } = useTastingStore();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<AcceptFormData>();

  // Fetch invitation data
  const { data: invitation, isLoading, error } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => invitationApi.get(token!),
    enabled: !!token,
    retry: false,
  });

  // Apply branding when invitation loads
  useEffect(() => {
    if (invitation?.branding) {
      applyBranding(invitation.branding);
    }
  }, [invitation, applyBranding]);

  // Accept mutation
  const acceptMutation = useMutation({
    mutationFn: async (data: AcceptFormData) => {
      const guest = await invitationApi.accept(token!, data.guestName);

      // Start tasting session
      const session = await tastingApi.startSession({
        venue_id: 0,
        tasting_type: 'event',
        guest_name: data.guestName,
        event_id: guest.event_id,
        guest_id: guest.id,
      });

      return { guest, session };
    },
    onSuccess: ({ guest, session }) => {
      setGuest(guest);
      setSession(session);
      resetTasting();
      toast.success('Einladung angenommen!');
      navigate(`/session/${session.uuid}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Decline mutation
  const declineMutation = useMutation({
    mutationFn: () => invitationApi.decline(token!),
    onSuccess: () => {
      toast.success('Einladung abgelehnt');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onAccept = async (data: AcceptFormData) => {
    setIsAccepting(true);
    try {
      await acceptMutation.mutateAsync(data);
    } finally {
      setIsAccepting(false);
    }
  };

  const onDecline = async () => {
    if (!confirm('Möchtest du die Einladung wirklich ablehnen?')) return;

    setIsDeclining(true);
    try {
      await declineMutation.mutateAsync();
    } finally {
      setIsDeclining(false);
    }
  };

  if (isLoading) {
    return (
      <TastingLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </TastingLayout>
    );
  }

  if (error || !invitation) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <AlertCircle className="w-16 h-16 text-danger-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            Einladung nicht gefunden
          </h1>
          <p className="text-dark-400 max-w-md">
            Diese Einladung ist ungültig oder abgelaufen.
          </p>
        </div>
      </TastingLayout>
    );
  }

  const isAlreadyResponded = invitation.guest_status !== 'invited';
  const isEventAvailable = invitation.event_status === 'scheduled' || invitation.event_status === 'active';

  return (
    <TastingLayout showBranding branding={invitation.branding}>
      <div className="max-w-lg mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Invitation Header */}
          <div className="text-primary-400 text-sm font-medium uppercase tracking-wider mb-2">
            Du bist eingeladen
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-bold text-dark-50 mb-4">
            {invitation.event_name}
          </h1>

          {/* Event Meta */}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-dark-400">
            {invitation.venue_name && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{invitation.venue_name}</span>
              </div>
            )}
            {invitation.scheduled_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(invitation.scheduled_at), 'PPPp', { locale: de })}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Status Messages */}
        {isAlreadyResponded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card text-center mb-8"
          >
            {invitation.guest_status === 'accepted' && (
              <>
                <Check className="w-12 h-12 text-success-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">
                  Einladung bereits angenommen
                </h2>
                <p className="text-dark-400 mb-4">
                  Du hast diese Einladung bereits angenommen.
                </p>
                <button
                  onClick={() => navigate(`/${invitation.event_uuid}`)}
                  className="btn-primary"
                >
                  Zum Event
                </button>
              </>
            )}
            {invitation.guest_status === 'declined' && (
              <>
                <X className="w-12 h-12 text-danger-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">
                  Einladung abgelehnt
                </h2>
                <p className="text-dark-400">
                  Du hast diese Einladung abgelehnt.
                </p>
              </>
            )}
            {invitation.guest_status === 'joined' && (
              <>
                <Check className="w-12 h-12 text-success-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">
                  Bereits beigetreten
                </h2>
                <p className="text-dark-400 mb-4">
                  Du nimmst bereits am Tasting teil.
                </p>
              </>
            )}
            {invitation.guest_status === 'completed' && (
              <>
                <Check className="w-12 h-12 text-primary-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">
                  Tasting abgeschlossen
                </h2>
                <p className="text-dark-400">
                  Du hast das Tasting erfolgreich abgeschlossen.
                </p>
              </>
            )}
          </motion.div>
        )}

        {!isAlreadyResponded && !isEventAvailable && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card text-center"
          >
            <AlertCircle className="w-12 h-12 text-warning-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-dark-100 mb-2">
              Event nicht verfügbar
            </h2>
            <p className="text-dark-400">
              {invitation.event_status === 'completed'
                ? 'Dieses Event wurde bereits abgeschlossen.'
                : 'Dieses Event ist derzeit nicht verfügbar.'}
            </p>
          </motion.div>
        )}

        {/* Accept Form */}
        {!isAlreadyResponded && isEventAvailable && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <h2 className="text-lg font-semibold text-dark-100 mb-4">Einladung annehmen</h2>
            <form onSubmit={handleSubmit(onAccept)} className="space-y-4">
              <div>
                <label className="label">Dein Name *</label>
                <input
                  type="text"
                  {...register('guestName', {
                    required: 'Bitte gib deinen Namen ein',
                    minLength: { value: 2, message: 'Name muss mindestens 2 Zeichen haben' },
                  })}
                  placeholder="Max Mustermann"
                  className="input"
                  disabled={isAccepting || isDeclining}
                />
                {errors.guestName && (
                  <p className="text-danger-400 text-sm mt-1">{errors.guestName.message}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isAccepting || isDeclining}
                  className="btn-primary flex-1"
                >
                  {isAccepting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Annehmen...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Annehmen
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onDecline}
                  disabled={isAccepting || isDeclining}
                  className="btn-secondary"
                >
                  {isDeclining ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <X className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </TastingLayout>
  );
}
